<template>
    <div class="search">
        <al-input
            :value="searchInput"
            @input="(e: Event) => searchInput = (e.target as HTMLInputElement).value"
            placeholder="Search"
            prepend-icon="search"
            data-testid="search"
        />
    </div>
    <div class="grid-controller">
        <div class="select-all">
            <div class="checkbox">
                <al-checkbox
                    :checked="selectAll"
                    @change="onSelectAllChange"
                    data-testid="checkbox"
                />
            </div>
            <div
                class="count"
                data-testid="selected-count"
            >
                {{ selectedCount }} selected
            </div>
        </div>
        <Dropdown
            text="Actions"
            v-slot="{ closeDropdown }"
            class="actions"
        >
            <DropdownOption
                text="Enable metrics"
                @pressed="onMetricsActionPress(true, closeDropdown)"
            />
            <DropdownOption
                text="Disable metrics"
                @pressed="onMetricsActionPress(false, closeDropdown)"
            />
        </Dropdown>
        <Dropdown
            icon="filter"
            v-slot="{ closeDropdown }"
            class="sort-grid-actions"
        >
            <DropdownOption
                text="See enabled metrics only"
                :selected="viewSelectedMetricsOnly === true"
                @pressed="onFilterMetricsBySelectionStatus(true, closeDropdown)"
            />
            <DropdownOption
                text="See disabled metrics only"
                :selected="viewSelectedMetricsOnly === false"
                @pressed="onFilterMetricsBySelectionStatus(false, closeDropdown)"
            />
        </Dropdown>
    </div>
</template>
<script setup lang="ts">
import {
    defineProps, ref, nextTick, watch
} from 'vue';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-alpine.css';
import { GridApi } from '@ag-grid-community/core';
import { Dropdown, DropdownOption } from '../BulkActions';
import { RowData, METRIC_STATUS } from '@/types';

interface Props {
    gridApi: GridApi<RowData> | null;
    selectAll: boolean;
    selectedCount: number;
}

const props = defineProps<Props>();
const searchInput = ref('');
const selectAll = ref<boolean>(props.selectAll);
const viewSelectedMetricsOnly = ref<boolean | null>(null);

/**
 * Record the current selected status filter & add the status filter
 * model to the grid.
 *
 * Note: If the value of `viewSelectedMetricsOnly` is the same as the incoming value of
 * `isMetricEnabled` then that status filter will be turned off.
 *
 * Also, we're using `getFilterInstance` so that we won't override existing filters.
 */
const onFilterMetricsBySelectionStatus = (isMetricEnabled: boolean, callback?: () => void) => {
    if (viewSelectedMetricsOnly.value === isMetricEnabled) {
        viewSelectedMetricsOnly.value = null; // disable on second press
        if (props.gridApi) {
            const instance = props.gridApi.getFilterInstance('status');
            instance?.setModel?.(null);
        }
    } else {
        viewSelectedMetricsOnly.value = isMetricEnabled;
        if (props.gridApi) {
            const instance = props.gridApi.getFilterInstance('status');
            instance?.setModel?.({
                filterType: 'text',
                type: 'equals',
                filter: METRIC_STATUS[+(viewSelectedMetricsOnly.value)]
            });
            props.gridApi.onFilterChanged();
        }
    }
    callback?.();
};

const onMetricsActionPress = async (enabled: boolean, callback?: () => void) => {
    if (props.gridApi) {
        const selectedNodes = props.gridApi.getSelectedNodes();
        const nodeMap = new Map(); // useful below with re-selection

        selectedNodes.forEach((node) => {
            if (node.data && node.data.metricDefinition) {
                nodeMap.set(node.data.metricDefinition.field, node.data.metricDefinition);
                node.data.metricDefinition.isSelected = enabled;
            }
        });

        // When the above code runs it will update the grid in such a way that de-selects all the
        // selected rows. This section re-selects them to provide a consistent UX.
        await nextTick();
        props.gridApi.forEachNode((node) => {
            if (node.data && node.data.metricDefinition) {
                if (nodeMap.has(node.data.metricDefinition.field)) {
                    node.setSelected(true);
                }
            }
        });
    }
    callback?.();
};

// Handle "Select All" toggle changes
// This will only select all of the currently displayed/filtered rows.
const onSelectAllChange = async (e: Event) => {
    if (props.gridApi) {
        if ((e.target as HTMLInputElement).checked) props.gridApi.selectAllFiltered();
        else props.gridApi.deselectAllFiltered();
    }
};

// Watch search filtering and update the table's filter model if there's search content.
watch(() => props.selectedCount, () => {
    if (props.selectedCount === 0) {
        selectAll.value = false;
    }
});
watch(() => props.selectAll, () => {
    selectAll.value = props.selectAll;
});
watch([ searchInput, viewSelectedMetricsOnly ], () => {
    if (props.gridApi) {
        const instance = props.gridApi.getFilterInstance('metrics');
        instance?.setModel?.(searchInput.value?.trim() ? {
            filterType: 'text',
            type: 'contains',
            filter: searchInput.value
        } : null);
        props.gridApi.onFilterChanged();
    }
    
});
</script>